/**
 * Export a scene item as a thin `.glayer` via the converter ad-hoc endpoint (WP-E).
 */

const CONVERTER_BASE =
  (import.meta.env.VITE_CONVERTER_API_BASE_URL as string | undefined)?.replace(/\/+$/, '') ||
  'http://127.0.0.1:3011';

export interface GlayerExportResult {
  readonly fileName: string;
  readonly glayerUrl: string;
  readonly blob: Blob;
}

export async function exportSceneItemAsGlayer(
  sceneItem: Record<string, unknown>,
  name: string,
): Promise<GlayerExportResult> {
  const res = await fetch(`${CONVERTER_BASE}/api/v1/converter/runs/ad-hoc/single-layer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ sceneItem, name }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Glayer export failed (${res.status}): ${detail.slice(0, 240)}`);
  }
  const body = (await res.json()) as { fileName?: string; glayerUrl?: string };
  const fileName = body.fileName ?? `${name}.glayer`;
  const glayerUrl = body.glayerUrl;
  if (!glayerUrl) throw new Error('Glayer export response missing glayerUrl');

  const fileRes = await fetch(glayerUrl);
  if (!fileRes.ok) {
    throw new Error(`Failed to download .glayer (${fileRes.status})`);
  }
  const blob = await fileRes.blob();
  return { fileName, glayerUrl, blob };
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}
